import React, { Context, createContext, useCallback, useContext } from 'react';
import { AlertPopup, Color } from '../components/alerts/AlertPopup.component';

interface AlertsContext {
  notify: (
    message: string, 
    color: Color, 
    onRevertActionButton?:() => void,
  ) => void;
  handleClose: (event?: React.SyntheticEvent, reason?: string) => void;
}
interface AlertParams {
  message: string;
  color: Color;
  onRevertActionButton?:() => void;
}

/**
 * Contexte pour la gestion des alertes et notifications
 * 
 * Ce contexte gère l'affichage des notifications dans l'application :
 * - Messages de succès, d'erreur, d'information
 * - Actions de réversion optionnelles
 * - Fermeture automatique ou manuelle
 * 
 * Logique métier :
 * - État global des alertes avec message et couleur
 * - Support des actions de réversion pour les opérations critiques
 * - Prévention de la fermeture accidentelle (clickaway)
 */
const alertsContext: Context<AlertsContext> = createContext(null) as any;

/**
 * Hook pour accéder au contexte des alertes
 * 
 * @returns Contexte des alertes avec fonctions notify et handleClose
 */
export function useAlerts() {
  return useContext(alertsContext);
}

/**
 * Provider des alertes
 * 
 * Ce provider :
 * 1. Gère l'état des alertes (message, couleur, ouverture)
 * 2. Fournit les fonctions notify et handleClose
 * 3. Affiche le composant AlertPopup
 * 4. Gère la fermeture avec prévention du clickaway
 * 
 * @param props Props des composants enfants
 * @returns Provider React des alertes
 */
export const ProvideAlerts: React.FC = props => {
  const [params, setParams] = React.useState<AlertParams>({
    message: '',
    color: 'success',
  });

  const [opened, setOpened] = React.useState(false);

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpened(false);
  };

  const notify = useCallback((message, color, onRevertActionButton?) => {
    setOpened(true);
    setParams({ message, color, onRevertActionButton });
  }, []);

  return (
    <alertsContext.Provider value={{ notify, handleClose }}>
      {props.children}
      <AlertPopup
        message={params?.message}
        color={params?.color}
        open={opened}
        handleClose={handleClose}
        onRevertActionButton={params?.onRevertActionButton}
      />
    </alertsContext.Provider>
  );
};
